import React from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  Button,
  Autocomplete,
  TextField,
  Checkbox,
} from "@mui/material";
import { StarRounded } from "@mui/icons-material";
import { AAMC_SPECIALTIES } from "@healthdesk/shared-services";

const Filters = ({
  setFilter,
  filter,
  ads = [],
  sx = {},
  style = {},
  onSortLevelOfCare,
  /** When false (e.g. /scmap), hide the government/restricted-access checkbox. */
  showRestrictedAccessToggle = true,
}) => {
  // Set default values if not already set
  React.useEffect(() => {
    setFilter((prev) => ({
      ...prev,
      facility: prev.facility || "all",
      rating: prev.rating || 0,
    }));
  }, [setFilter]);

  // Determine if we should show star rating select
  const showStarRating = filter.facility === "emergency";
  const showSpecialtyFilter = filter.facility === "clinic";

  // Helper function for handling filter changes
  const handleFilterChange = (field) => (event) => {
    setFilter((prev) => {
      const value = event.target.value;
      const newState = {
        ...prev,
        [field]: value,
        ...(field === "facility" &&
          value !== "emergency" && { rating: "" }),
        ...(field === "facility" &&
          value === "emergency" && { group: "", rating: 1, specialty: "" }),
        ...(field === "facility" &&
          value === "clinic" && { specialty: prev.specialty || "" }),
        ...(field === "facility" &&
          value === "urgent_care" && { group: "", specialty: "" }),
      };
      return newState;
    });
  };

  // Handle specialty filter change
  const handleSpecialtyChange = (event, newValue) => {
    setFilter((prev) => ({
      ...prev,
      specialty: newValue ? newValue.value : "",
    }));
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        maxWidth: "500px",
        margin: "auto",
        marginBottom: { xs: 0, sm: 2 },
        ...sx,
      }}
      style={style}
    >
      {/* Left Column - Radio Buttons */}
      <Box
        sx={{
          width: "50%",
          padding: "0 8px 0 0",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            height: "100%",
            gap: { xs: 0.5, sm: 0 },
          }}
        >
          <Typography
            variant="h6"
            color="primary"
            sx={{
              whiteSpace: "nowrap",
              marginRight: { xs: 0, sm: "3px" },
              fontSize: "1.125rem",
              fontWeight: "500",
            }}
          >
            I need care:
          </Typography>
          <RadioGroup
            value={filter.facility || "all"}
            onChange={handleFilterChange("facility")}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 0.5, sm: 0 },
            }}
          >
            <FormControlLabel
              value="clinic"
              control={<Radio size="small" />}
              label="Clinics (appointments)"
              sx={{
                margin: 0,
                height: "24px",
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.875rem",
                },
              }}
            />
            <FormControlLabel
              value="urgent_care"
              control={<Radio size="small" />}
              label="Urgent Care"
              sx={{
                margin: 0,
                height: "24px",
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.875rem",
                },
              }}
            />
            <FormControlLabel
              value="emergency"
              control={<Radio size="small" />}
              label="Immediately"
              sx={{
                margin: 0,
                height: "24px",
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.875rem",
                },
              }}
            />
          </RadioGroup>
          {showRestrictedAccessToggle ? (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={Boolean(filter.showRestrictedAccess)}
                  onChange={(event) =>
                    setFilter((prev) => ({
                      ...prev,
                      showRestrictedAccess: event.target.checked,
                    }))
                  }
                />
              }
              label="Show government/restricted-access facilities (VA, military, IHS)"
              sx={{
                mt: 1,
                alignItems: "flex-start",
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.75rem",
                  lineHeight: 1.3,
                },
              }}
            />
          ) : null}
        </Box>
      </Box>

      {/* Right Column - Star Rating, Specialty Filter, or Help Button */}
      <Box
        sx={{
          width: "50%",
          padding: "0 0 0 8px",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          height: { xs: "auto", sm: "48px" },
          marginTop: { xs: 0, sm: 0 },
        }}
      >
        {showStarRating ? (
          <FormControl
            size="small"
            sx={{
              width: "100%",
              "& .MuiInputBase-root": {
                height: "40px",
              },
            }}
          >
            <InputLabel id="star-rating-label">Star Rating</InputLabel>
            <Select
              labelId="star-rating-label"
              id="star-rating"
              value={filter.rating || 1}
              label="Star Rating"
              onChange={handleFilterChange("rating")}
              sx={{
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              {[1, 5, 4, 3, 2].map((value) => (
                <MenuItem key={value} value={value}>
                  {value === 1 ? (
                    "All"
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {[...Array(value)].map((_, i) => (
                        <StarRounded
                          key={i}
                          sx={{
                            color: "#FFC404",
                            width: "1.25rem",
                            height: "1.25rem",
                          }}
                        />
                      ))}
                      {value !== 5 && (
                        <span style={{ paddingLeft: "3px" }}>+</span>
                      )}
                    </Box>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : showSpecialtyFilter ? (
          <Autocomplete
            size="small"
            options={AAMC_SPECIALTIES}
            value={AAMC_SPECIALTIES.find(s => s.value === filter.specialty) || null}
            onChange={handleSpecialtyChange}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by Specialty"
                placeholder="All Specialties"
              />
            )}
            sx={{
              width: "100%",
              "& .MuiInputBase-root": {
                height: "40px",
              },
            }}
          />
        ) : (
          <Button
            onClick={onSortLevelOfCare}
            variant="outlined"
            size="small"
            sx={{
              fontSize: "13px",
              width: "100%",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Help Me Choose
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default Filters;
